/* eslint-disable max-lines */
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import Box from '@mui/material/Box'
import MuiTab from '@mui/material/Tab'
import MuiTabs, { tabsClasses } from '@mui/material/Tabs'
import { styled } from '@mui/material/styles'
import styledComponent from 'styled-components'

import KeyboardAccessible from 'components/KeyboardAccessible'
import TabPanel from 'components/Tabs/TabPanel'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

export interface Tab {
  key: string
  header: ReactNode
  details: ReactNode
  alerts?: number
  badge?: number
}

type Props = {
  activeTabIndex?: number
  tabItems: Array<Tab>
  onSelectTabIndexCallback?: (index: number) => void
  variant?: string
  tabpage?: 'device' | 'site_message' | 'others'
  renderMode?: 'all' | 'headersOnly' | 'contentOnly'
}

const Tabs = ({
  tabItems,
  onSelectTabIndexCallback = () => {},
  activeTabIndex = 0,
  variant = 'scrollable',
  tabpage = 'others',
  renderMode = 'all'
}: Props) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(activeTabIndex)
  const [hasScrollButtons, setHasScrollButtons] = useState(false)
  const tabsRef = useRef<HTMLDivElement>(null)

  const tabId = (key: string) => `tab-${key}`
  const tabPanelId = (key: string) => `tab-panel-${key}`

  const setSelectedTab = useCallback((newIndex: number) => {
    if (newIndex === selectedTabIndex) return

    setSelectedTabIndex(newIndex)
    onSelectTabIndexCallback(newIndex)
  }, [selectedTabIndex, onSelectTabIndexCallback])

  useEffect(() => {
    setSelectedTabIndex(activeTabIndex)
  }, [activeTabIndex])

  useEffect(() => {
    const checkScrollButtons = () => {
      if (!tabsRef.current) return

      const scrollButtons = tabsRef.current.querySelectorAll('.MuiTabScrollButton-root:not(.Mui-disabled)')
      setHasScrollButtons(scrollButtons.length > 0)
    }

    const observer = new MutationObserver(checkScrollButtons)

    if (tabsRef.current) {
      observer.observe(tabsRef.current, {
        attributes: true,
        subtree: true,
        childList: true
      })
      checkScrollButtons()
    }

    window.addEventListener('resize', checkScrollButtons)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', checkScrollButtons)
    }
  }, [tabItems])

  return (
    <S.TabContainer tabpage={tabpage}>
      {renderMode !== 'contentOnly' && (
        <S.Tabs
          ref={tabsRef}
          value={selectedTabIndex}
          variant={variant}
          scrollButtons="auto"
          allowScrollButtonsMobile
          tabpage={tabpage}
        >
          {tabItems.map((item, index) => (
            <S.KeyboardAccessible
              key={item.key}
              onClick={() => setSelectedTab(index)}
              tabpage={tabpage}
              selected={selectedTabIndex === index}
            >
              <S.Tab
                tabIndex={-1}
                tabpage={tabpage}
                alerts={item.alerts}
                disableRipple
                label={
                  (item.badge !== undefined && item.badge >= 0)
                    ? (
                      <S.Header>
                        <S.Box selected={selectedTabIndex === index}>{item.badge}</S.Box>
                        {item.header}
                      </S.Header>
                    )
                    : item.header
                }
                id={tabId(item.key)}
                aria-controls={tabPanelId(item.key)}
                selected={selectedTabIndex === index}
              />
            </S.KeyboardAccessible>
          ))}
        </S.Tabs>
      )}

      {renderMode !== 'headersOnly' && tabItems.map((item, index) => (
        <S.TabPanel
          key={item.key}
          tabpage={tabpage}
          hasScrollButtons={hasScrollButtons}
          hidden={index !== selectedTabIndex}
          id={tabPanelId(item.key)}
          ariaLabelledby={tabId(item.key)}
        >
          {item.details}
        </S.TabPanel>
      ))}
    </S.TabContainer>
  )
}

export default Tabs

const S = {
  TabContainer: styledComponent.div.withConfig({
    shouldForwardProp: (prop) => !['tabpage'].includes(prop)
  })<{ tabpage: string }>`
    display: grid;
    border-radius: 5px;
  `,
  TabPanel: styledComponent(TabPanel).withConfig({
    shouldForwardProp: (prop) => !['tabpage', 'hasScrollButtons'].includes(prop)
  })<{ tabpage: string; hasScrollButtons: boolean }>`
    width: 100%;
    overflow: auto;
    min-height: 55vh;
    border-radius: ${({ hasScrollButtons }) => (hasScrollButtons ? '16px' : '0px 16px 16px 16px')};
    background-color: ${color('paneBackground')};
    border: ${({ tabpage }) => (tabpage !== 'device' ? `1px solid ${color('border')}` : 'none')};
  `,
  Tabs: styled(MuiTabs, {
    shouldForwardProp: (prop: string) => !['tabpage'].includes(prop)
  })<{ tabpage: string }>`
    min-height: 42px;
    position: ${({ tabpage }) => tabpage === 'device' ? 'static' : 'relative'};
    z-index: ${({ tabpage }) => tabpage === 'device' ? 'auto' : '1'};
    margin-bottom: ${({ tabpage }) => tabpage === 'device' ? '0' : '-1px'};

    & .MuiTabs-indicator {
      background-color: ${color('paneBackground')};
    }

    ${() => `
      & .${tabsClasses.scrollButtons} {
        align-self: center;

        &.Mui-disabled {
          opacity: 0.3;
        }

        & svg {
          font-size: 40px;
        }
      }
    `}
  `,
  KeyboardAccessible: styledComponent(KeyboardAccessible).withConfig({
    shouldForwardProp: (prop) => !['tabpage', 'selected'].includes(prop)
  })<{ tabpage: string; selected: boolean }>`
    ${({ tabpage }) => (tabpage === 'device'
    ? `
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
        border: 1px solid ${color('dashboardCardBorder')}; 
      `
    : `
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      `)}
  `,
  Tab: styled(MuiTab, {
    shouldForwardProp: (prop: string) => !['alerts', 'tabpage'].includes(prop)
  })<{ alerts?: number; tabpage: string }>`
    font-family: Inter, sans-serif;
    font-size: 14px;
    padding: ${({ tabpage }) => tabpage === 'device' ? '0' : '12px 16px'};
    color: ${color('cardTitle')};
    background-color: ${color('modalGridBackground')};
    border: ${({ tabpage }) => tabpage !== 'device' ? `1px solid ${color('border')}` : 'none'};
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    min-height: ${({ tabpage }) => tabpage === 'device' ? '55px' : '42px'};
    text-transform: capitalize;
    white-space: nowrap;
    opacity: 1;

    ${({ tabpage }) => tabpage === 'device' && `
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    `}

    &.Mui-selected {
      border-bottom: 1px solid ${color('paneBackground')};
      position: relative;
      z-index: 2;
      background-color: ${color('paneBackground')};
      color: ${color('primaryText')};
    }

    &:focus {
      outline: none;
    }

    @media ${screenSize.medium} {
      font-size: 12px;
    }
  `,
  Box: styled(Box)<{ selected: boolean }>`
    width: 18px;
    height: 16px;
    min-width: 18px;
    border-radius: 8px;
    ${({ selected }) => (selected
    ? `
        background-color: ${color('primaryButton')};
        color: ${color('primaryButtonText')};
      `
    : `
        background-color: ${color('secondaryButtonText')};
        color: ${color('secondaryButton')};
      `)}
  `,
  Header: styledComponent.div`
    display: flex;
    align-items: center;
    gap: 5px;

    @media ${screenSize.medium} {
      font-size: 12px;
    }
  `
}
